/** API do assistente, /api/v1/assistente. Contrato fechado em 05/09/2026 com o
 *  backend, que está sendo escrito sobre estes mesmos nomes: quem mudar um campo
 *  aqui muda lá, e vice-versa.
 *
 *  O envio da pergunta NÃO passa por aqui: é um fluxo SSE aberto com `fetch` em
 *  `useFluxo.ts`, porque o axios não lê fluxo e `EventSource` não manda o Bearer.
 *  Tudo o mais, histórico, renomear, apagar, parar e avaliar, usa o `http` comum. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http, resolveApiBaseUrl } from '@/core/api/client'
import type { Lang } from '@/core/i18n/messages'

export const ASSISTENTE_KEY = 'assistente'

// ── Contexto da tela ─────────────────────────────────────────────────

/** O que a tela publica para o assistente: só o que ela já mostra. Entra na
 *  mensagem do usuário, nunca no prompt do sistema, e não abre porta nenhuma: a
 *  ferramenta de SQL segue checando permissão por view. */
export interface ContextoDaTela {
  tela: string
  rota: string
  periodo?: { de: string; ate: string }
  filtros?: Record<string, string>
  entidade?: { tipo: string; nome: string; uuid?: string }
}

// ── Mensagens e conversas ────────────────────────────────────────────

export type PapelDaMensagem = 'usuario' | 'assistente'

export interface EtapaDaResposta {
  id: string
  rotulo: string
  estado: 'andamento' | 'concluida'
}

export interface ConsultaDaResposta {
  sql: string
  objetivo: string
  /** Linhas lidas pela consulta. */
  linhas: number
}

export interface MensagemDaConversa {
  uuid: string
  papel: PapelDaMensagem
  texto: string
  etapas: EtapaDaResposta[]
  consultas: ConsultaDaResposta[]
  created_at: string
}

/** Item de GET /conversas. */
export interface ConversaResumo {
  uuid: string
  titulo: string | null
  created_at: string
  updated_at: string
  primeira_pergunta: string | null
  arquivada: boolean
}

/** GET /conversas/:uuid, para reabrir onde parou. */
export interface ConversaDetalhe {
  uuid: string
  titulo: string | null
  mensagens: MensagemDaConversa[]
}

/** Corpo de POST /conversas/:uuid/mensagens. O idioma vai junto para o
 *  assistente responder nele. */
export interface EnviarMensagemCorpo {
  texto: string
  contexto: ContextoDaTela | null
  idioma: Lang
}

export interface AvaliacaoCorpo {
  util: boolean
  comentario?: string
}

/** Teto diário por usuário: 429 com este código e `volta_em` em ISO. */
export const CODIGO_LIMITE_DIARIO = 'ASSISTENTE_LIMITE_DIARIO'

export interface ErroDoServidor {
  error?: string
  code?: string
  volta_em?: string
}

// ── Eventos do fluxo SSE ─────────────────────────────────────────────

export interface UsoDoTurno {
  tokens_entrada?: number
  tokens_cache?: number
  tokens_saida?: number
  duracao_ms?: number
}

/** Os sete eventos da seção 6 da interface. Um comentário SSE vazio a cada
 *  quinze segundos mantém a conexão viva e não chega até aqui. */
export type EventoFluxo =
  | { nome: 'inicio'; dados: { uuid: string } }
  | { nome: 'etapa'; dados: EtapaDaResposta }
  | { nome: 'sql'; dados: ConsultaDaResposta }
  | { nome: 'texto'; dados: { trecho: string } }
  | { nome: 'titulo'; dados: { titulo: string } }
  | { nome: 'fim'; dados: UsoDoTurno }
  | { nome: 'erro'; dados: { code: string; mensagem: string } }

// ── Chamadas ─────────────────────────────────────────────────────────

export const assistenteApi = {
  /** As ativas por padrão; `arquivadas` é a aba Arquivadas do histórico. */
  listarConversas: (arquivadas = false) =>
    http.get<ConversaResumo[]>(arquivadas ? '/assistente/conversas?arquivadas=1' : '/assistente/conversas'),
  criarConversa: () => http.post<{ uuid: string }>('/assistente/conversas', {}),
  carregarConversa: (uuid: string) => http.get<ConversaDetalhe>(`/assistente/conversas/${uuid}`),
  renomearConversa: (uuid: string, titulo: string) =>
    http.patch<unknown>(`/assistente/conversas/${uuid}`, { titulo }),
  arquivarConversa: (uuid: string) =>
    http.patch<unknown>(`/assistente/conversas/${uuid}`, { arquivada: true }),
  desarquivarConversa: (uuid: string) =>
    http.patch<unknown>(`/assistente/conversas/${uuid}`, { arquivada: false }),
  apagarConversa: (uuid: string) => http.del<unknown>(`/assistente/conversas/${uuid}`),
  /** Limpar histórico: todas as conversas do usuário, ativas e arquivadas. */
  apagarTodas: () => http.del<{ deleted: boolean; conversas: number }>('/assistente/conversas'),
  /** Tira da conversa o turno da mensagem: a pergunta e a resposta. */
  apagarTurno: (mensagemUuid: string) =>
    http.del<{ deleted: boolean; mensagens: number }>(`/assistente/mensagens/${mensagemUuid}`),
  pararTurno: (uuid: string) => http.post<unknown>(`/assistente/conversas/${uuid}/parar`, {}),
  avaliarMensagem: (mensagemUuid: string, corpo: AvaliacaoCorpo) =>
    http.post<unknown>(`/assistente/mensagens/${mensagemUuid}/avaliacao`, corpo),
  /** Endereço absoluto do fluxo, montado sobre a mesma base do axios. */
  urlDeMensagens: (conversaUuid: string) =>
    `${resolveApiBaseUrl()}/assistente/conversas/${conversaUuid}/mensagens`,
}

/** Uma lista por aba do histórico: ativas e arquivadas são consultas
 *  separadas, e `chaveDeTodasAsListas` é o prefixo que invalida as duas. */
export const chaveDeTodasAsListas = () => [ASSISTENTE_KEY, 'conversas'] as const
export const chaveDasConversas = (arquivadas = false) =>
  [ASSISTENTE_KEY, 'conversas', arquivadas ? 'arquivadas' : 'ativas'] as const
export const chaveDaConversa = (uuid: string) => [ASSISTENTE_KEY, 'conversa', uuid] as const

// ── Hooks ────────────────────────────────────────────────────────────

export function useConversas(habilitado = true, arquivadas = false) {
  return useQuery({
    queryKey: chaveDasConversas(arquivadas),
    queryFn: () => assistenteApi.listarConversas(arquivadas),
    enabled: habilitado,
  })
}

export function useConversa(uuid: string | null, habilitado = true) {
  return useQuery({
    queryKey: chaveDaConversa(uuid ?? ''),
    queryFn: () => assistenteApi.carregarConversa(uuid as string),
    enabled: habilitado && Boolean(uuid),
  })
}

export function useRenomearConversa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, titulo }: { uuid: string; titulo: string }) => assistenteApi.renomearConversa(uuid, titulo),
    onSuccess: (_r, { uuid, titulo }) => {
      qc.setQueryData<ConversaDetalhe>(chaveDaConversa(uuid), (atual) => (atual ? { ...atual, titulo } : atual))
      qc.setQueriesData<ConversaResumo[]>({ queryKey: chaveDeTodasAsListas() }, (lista) =>
        lista?.map((c) => (c.uuid === uuid ? { ...c, titulo } : c)))
    },
  })
}

/** Arquivar tira da lista de ativas; desarquivar tira da de arquivadas. A
 *  lista de destino é invalidada em vez de remontada, porque a ordem dela é a
 *  do servidor. */
export function useArquivarConversa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => assistenteApi.arquivarConversa(uuid),
    onSuccess: (_r, uuid) => {
      qc.setQueryData<ConversaResumo[]>(chaveDasConversas(false), (lista) => lista?.filter((c) => c.uuid !== uuid))
      void qc.invalidateQueries({ queryKey: chaveDasConversas(true) })
    },
  })
}

export function useDesarquivarConversa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => assistenteApi.desarquivarConversa(uuid),
    onSuccess: (_r, uuid) => {
      qc.setQueryData<ConversaResumo[]>(chaveDasConversas(true), (lista) => lista?.filter((c) => c.uuid !== uuid))
      void qc.invalidateQueries({ queryKey: chaveDasConversas(false) })
    },
  })
}

export function useApagarConversa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uuid: string) => assistenteApi.apagarConversa(uuid),
    onSuccess: (_r, uuid) => {
      qc.setQueriesData<ConversaResumo[]>({ queryKey: chaveDeTodasAsListas() }, (lista) =>
        lista?.filter((c) => c.uuid !== uuid))
      qc.removeQueries({ queryKey: chaveDaConversa(uuid) })
    },
  })
}

export function useApagarTodas() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => assistenteApi.apagarTodas(),
    onSuccess: () => {
      qc.setQueriesData<ConversaResumo[]>({ queryKey: chaveDeTodasAsListas() }, () => [])
      qc.removeQueries({ queryKey: [ASSISTENTE_KEY, 'conversa'] })
    },
  })
}

export function useAvaliarMensagem() {
  return useMutation({
    mutationFn: ({ mensagemUuid, corpo }: { mensagemUuid: string; corpo: AvaliacaoCorpo }) =>
      assistenteApi.avaliarMensagem(mensagemUuid, corpo),
  })
}
